import { tryGetDirectory, readFileHandles, readFilePrefix, writeFile } from "../../storage/fsa.ts";
import { writeModuleIndex } from "../shared/module-index.ts";
import { parseHeaders } from "./mime-headers.ts";
import { folderRelativeDir, folderIndexPath } from "./paths.ts";

/**
 * Rebuilds `_index.json` for one folder from the `.eml` files already on
 * disk (ARCHITECTURE.md §7) — the same idempotent, network-free,
 * read-from-disk-not-memory pattern as Teams' index.html render pass.
 *
 * Only the first `HEADER_READ_BYTES` of each file is read, not the whole
 * thing — an .eml can carry multi-megabyte base64 attachments after its
 * headers, and rebuilding the index should not mean re-reading every
 * attachment in the folder on every call. Real header blocks are near-always
 * well under this; a message whose headers somehow exceed it may be missing
 * from the index (a disclosed limitation, not a silent one) but its .eml is
 * still on disk untouched — the index is a convenience, not the export.
 */
const HEADER_READ_BYTES = 16_384;

export interface FolderIndexEntry {
    filename: string;
    subject: string | null;
    from: string | null;
    date: string | null;
}

function parseDateSafe(value: string | undefined): number {
    if (!value) {
        return 0;
    }
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Rebuilds one folder's `_index.json`. Returns the same entries so the caller
 * (exportMail) can fold them into the mailbox-wide mail/index.html without a
 * second disk read — every folder already goes through this once per run.
 */
export async function renderFolderIndex(
    root: FileSystemDirectoryHandle,
    mailboxLabel: string,
    folderPath: readonly string[]
): Promise<FolderIndexEntry[]> {
    const dir = await tryGetDirectory(root, folderRelativeDir(mailboxLabel, folderPath).split("/"));
    if (!dir) {
        return [];
    }

    const entries: FolderIndexEntry[] = [];
    for await (const { name, handle } of readFileHandles(dir)) {
        if (!name.endsWith(".eml")) {
            continue;
        }
        const prefix = await readFilePrefix(handle, HEADER_READ_BYTES);
        const headers = parseHeaders(prefix);
        entries.push({
            filename: name,
            subject: headers.subject ?? null,
            from: headers.from ?? null,
            date: headers.date ?? null
        });
    }

    // The Date header is RFC 5322 ("Thu, 14 Mar 2024 14:30:22 +0000"), not
    // sortable as a string — parsed to a timestamp instead. A message with no
    // usable date sorts first rather than being dropped from ordering.
    entries.sort((a, b) => parseDateSafe(a.date ?? undefined) - parseDateSafe(b.date ?? undefined));

    await writeFile(
        root,
        folderIndexPath(mailboxLabel, folderPath),
        JSON.stringify(entries, null, 2)
    );

    return entries;
}

export interface MailFolderEntries {
    folderPath: readonly string[];
    entries: readonly FolderIndexEntry[];
}

function formatMailDate(value: string | null): string {
    if (!value) {
        return "";
    }
    const ms = parseDateSafe(value);
    return ms > 0 ? new Date(ms).toLocaleString() : value;
}

/**
 * Rebuilds `mail/index.html` — one row per message across every folder —
 * from the per-folder entries `renderFolderIndex` already produced this run.
 * Idempotent and network-free, same pattern as Teams' global index.
 */
export async function renderMailIndex(
    root: FileSystemDirectoryHandle,
    mailboxLabel: string,
    folders: readonly MailFolderEntries[]
): Promise<void> {
    const rows = folders.flatMap(({ folderPath, entries }) =>
        entries.map((e) => {
            const label = folderPath.join("/");
            const dir = folderRelativeDir(mailboxLabel, folderPath);
            return {
                cells: {
                    subject: e.subject || "(no subject)",
                    from: e.from ?? "",
                    date: formatMailDate(e.date),
                    folder: label
                },
                sortValues: { date: parseDateSafe(e.date ?? undefined) },
                href: `${dir}/${e.filename}`
            };
        })
    );

    await writeModuleIndex(root, {
        moduleId: "mail",
        itemKind: "file",
        title: "Mail",
        subtitle: `${mailboxLabel} — ${rows.length} message${rows.length === 1 ? "" : "s"} exported`,
        columns: [
            { key: "subject", label: "Subject" },
            { key: "from", label: "From" },
            { key: "date", label: "Date" },
            { key: "folder", label: "Folder" }
        ],
        rows,
        emptyMessage: "No messages exported yet.",
        searchPlaceholder: "Filter messages…"
    });
}
